import React, { useState, useEffect } from 'react';
// import { Link } from 'react-router-dom';
import { FiSearch, FiArrowRight, FiCheck, FiBookOpen, FiCpu, FiZap, FiLayers, FiGlobe, FiMail } from 'react-icons/fi';
import Footer from '../../components/Footer';
import EmailGate from '../../components/EmailGate';
import { subscribe } from '../../api/subscriberApi';

const ACCENT = '#0080ff';
const GLOW = 'rgba(0,128,255,0.15)';

const RAINBOW = [
  '#ff0040','#ff4000','#ff8000','#ffc000',
  '#ffff00','#80ff00','#00ff80','#00ffff',
  '#0080ff','#4000ff','#8000ff','#ff00ff',
];

function rainbowText(text) {
  const chars = text.split('');
  const vis = chars.filter(c => c !== ' ').length;
  let ci = 0;
  return chars.map((ch, i) => {
    if (ch === ' ') return <span key={i}>&nbsp;</span>;
    const color = RAINBOW[Math.floor((ci / vis) * RAINBOW.length) % RAINBOW.length];
    ci++;
    return <span key={i} style={{ color }}>{ch}</span>;
  });
}

function TypingDemo({ texts, interval = 4000 }) {
  const [idx, setIdx] = useState(0);
  const [shown, setShown] = useState(0);
  const [erasing, setErasing] = useState(false);

  useEffect(() => {
    const text = texts[idx];
    if (!erasing) {
      if (shown < text.length) {
        const t = setTimeout(() => setShown(s => s + 1), 30 + Math.random() * 25);
        return () => clearTimeout(t);
      }
      const t = setTimeout(() => setErasing(true), interval);
      return () => clearTimeout(t);
    } else {
      if (shown > 0) {
        const t = setTimeout(() => setShown(s => s - 1), 15);
        return () => clearTimeout(t);
      }
      setErasing(false);
      setIdx(i => (i + 1) % texts.length);
    }
  }, [shown, erasing, idx, texts, interval]);

  return <span>{texts[idx].slice(0, shown)}<span className="pp2-cursor">|</span></span>;
}

const features = [
  { icon: <FiCpu />, title: 'AI-Powered Discovery', desc: 'LLMs understand research context, not just keywords. Find papers by concept, methodology, or finding.', tag: 'CORE' },
  { icon: <FiBookOpen />, title: 'Instant Summaries', desc: 'Structured paper summaries with key findings, methodology, and relevance scores in seconds.', tag: 'AI' },
  { icon: <FiLayers />, title: 'Smart Collections', desc: 'Intelligent collections that auto-update as new relevant work is published.', tag: 'AUTO' },
  { icon: <FiZap />, title: 'Citation Graph', desc: 'Visualize citation networks to understand research lineage and identify seminal works.', tag: 'VIZ' },
  { icon: <FiGlobe />, title: 'Multi-Source', desc: 'Searches arXiv, PubMed, Semantic Scholar, and institutional repositories simultaneously.', tag: 'DATA' },
  { icon: <FiSearch />, title: 'Natural Language', desc: 'Ask in plain English. SearchUp translates intent into precise academic search strategies.', tag: 'NLP' },
];

const betaPerks = [
  'Full access to all features during beta',
  'Direct input on the product roadmap',
  'Priority support from the engineering team',
  'Free forever at the beta tier when we launch',
];

const demoQueries = [
  'latest breakthroughs in multi-agent LLM orchestration',
  'efficient transformer architectures for edge deployment',
  'reinforcement learning from human feedback survey',
  'protein structure prediction using diffusion models',
];

export default function SearchUpPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleBetaSignup = (e, gUser) => {
    e.preventDefault();
    const finalEmail = gUser?.email || email;
    if (!finalEmail.trim()) return;
    subscribe({ email: finalEmail, name: gUser?.name, picture: gUser?.picture, source: 'searchup-beta', group: 'beta-searchup' });
    setSubmitted(true);
  };

  return (
    <div className="product-page pp2-page" style={{'--pp-accent': ACCENT, '--pp-glow': GLOW}}>
      {/* Hero */}
      <section className="pp2-hero">
        <div className="pp2-hero-inner">
          <div className="pp2-badge"><FiSearch /> SearchUp</div>
          <h1 className="pp2-title">{rainbowText('SearchUp')}</h1>
          <p className="pp2-tagline-label">AI Research Assistant</p>
          <p className="pp2-subtitle">
            Find any paper. Understand any field. In minutes, not hours.
          </p>
          <div className="pp2-beta-banner">
            <span className="pp2-beta-tag">BETA</span>
            <span>Now accepting early access signups — free during beta</span>
          </div>
          <div className="pp2-actions">
            <a href="#beta-signup" className="pp2-btn-primary">Join the Beta</a>
            <button className="pp2-btn-ghost">Learn More <FiArrowRight /></button>
          </div>
        </div>
      </section>

      {/* Search demo */}
      <section className="pp2-demo-section">
        <div className="pp2-demo-bar">
          <FiSearch className="pp2-demo-icon" />
          <span className="pp2-demo-text">
            <TypingDemo texts={demoQueries} interval={3000} />
          </span>
        </div>
      </section>

      {/* Features */}
      <section className="pp2-features">
        <div className="pp2-features-inner">
          <h2>Built for serious researchers</h2>
          <p className="pp2-features-sub">Every feature reduces the time from question to insight.</p>
          <div className="pp2-features-grid">
            {features.map(f => (
              <div key={f.title} className="pp2-feature-card">
                <div className="pp2-feature-head">
                  <div className="pp2-feature-icon">{f.icon}</div>
                  <span className="pp2-feature-tag">{f.tag}</span>
                </div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Beta Signup */}
      <section className="pp2-beta-section" id="beta-signup">
        <div className="pp2-beta-inner">
          <h2>Get Early Access</h2>
          <p className="pp2-beta-sub">
            SearchUp is in private beta. Sign up to get access — no credit card, no commitment.
            Beta users get free access and a direct line to shape the product.
          </p>
          <div className="pp2-beta-perks">
            {betaPerks.map(perk => (
              <div key={perk} className="pp2-beta-perk">
                <FiCheck /> <span>{perk}</span>
              </div>
            ))}
          </div>
          {!submitted ? (
            <EmailGate prompt="Sign in with Google to join the beta — we only use your email.">
              {(gUser) => (
                <form className="pp2-beta-form" onSubmit={(e) => handleBetaSignup(e, gUser)}>
                  <div className="pp2-beta-input-wrap">
                    <FiMail className="pp2-beta-input-icon" />
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={gUser.email || email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      readOnly={!!gUser.email}
                    />
                  </div>
                  <button type="submit" className="pp2-btn-primary">Request Beta Access</button>
                </form>
              )}
            </EmailGate>
          ) : (
            <div className="pp2-beta-success">
              <FiCheck /> <span>You're on the list! We'll reach out soon with your beta invite.</span>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
